import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, PieChart, Lock } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const Results = () => {
  const [stats, setStats] = useState(null);
  const [surveys, setSurveys] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/survey-results").then((r) => setSurveys(r.data)).catch(() => setSurveys([]));
  }, []);

  const statItems = [
    { value: stats?.responses, label: "survey answers" },
    { value: stats?.news, label: "headlines posted" },
    { value: stats?.teams, label: "teams on the table" },
  ];

  return (
    <div className="min-h-screen bg-cream dark:bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-12 md:py-16">
        <Reveal>
          <Eyebrow>The findings so far</Eyebrow>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter">
            What Morbi <span className="font-serif italic font-medium marker-highlight">said</span>
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Live results from the survey — this page updates every time someone answers. The
            numbers I'll present at school come from here.
          </p>
        </Reveal>

        <Reveal delay={0.06}>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="results-stats">
            {statItems.map((s) => (
              <div key={s.label} className="rounded-2xl bg-card border border-foreground/10 px-5 py-3">
                <div className="font-display text-2xl font-bold text-pitch dark:text-turf">{s.value ?? "—"}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {surveys === null ? (
          <div className="mt-12 grid place-items-center py-16 text-muted-foreground" data-testid="results-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : surveys.length === 0 ? (
          <div className="mt-10 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="results-empty">
            <p className="font-display font-bold text-lg">No results yet</p>
            <p className="text-sm text-muted-foreground mt-1">Be the first — take the survey on the home page.</p>
          </div>
        ) : (
          <div className="mt-12 space-y-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
              <PieChart className="h-6 w-6 text-flame" /> Survey findings
            </h2>
            {surveys.map((s) => (
              <div key={s.id} data-testid="results-survey-card" className="rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 shadow-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-display text-xl font-bold tracking-tight">{s.title}</h3>
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    {s.total_responses} {s.total_responses === 1 ? "response" : "responses"}
                  </span>
                </div>
                <div className="mt-6 space-y-7">
                  {s.questions
                    .filter((q) => q.type === "choice")
                    .map((q, qi) => {
                      const total = (q.counts || []).reduce((a, b) => a + b, 0);
                      const max = Math.max(1, ...(q.counts || [1]));
                      return (
                        <div key={q.id}>
                          <p className="font-semibold text-sm sm:text-base mb-3">
                            <span className="font-mono text-xs text-flame mr-2">{String(qi + 1).padStart(2, "0")}</span>
                            {q.text}
                          </p>
                          <div className="space-y-3" data-testid="results-bars">
                            {q.options.map((o, oi) => {
                              const count = (q.counts || [])[oi] || 0;
                              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                              const leader = count > 0 && count === Math.max(...(q.counts || [0]));
                              return (
                                <div key={o}>
                                  <div className="flex justify-between text-sm mb-1.5 gap-3">
                                    <span className="font-medium">{o}</span>
                                    <span className="font-mono text-xs text-muted-foreground shrink-0">
                                      {count} · {pct}%
                                    </span>
                                  </div>
                                  <div className="h-3.5 rounded-full bg-muted overflow-hidden" data-testid="results-bar">
                                    <motion.div
                                      initial={{ width: 0 }}
                                      whileInView={{ width: `${(count / max) * 100}%` }}
                                      viewport={{ once: true, amount: 0.6 }}
                                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                      className={`h-full rounded-full ${leader ? "bg-flame" : "bg-pitch dark:bg-turf"}`}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground pt-1">
                            {total} {total === 1 ? "answer" : "answers"}
                          </p>
                        </div>
                      );
                    })}
                </div>
                {s.questions.some((q) => q.type === "text" && q.answer_count > 0) && (
                  <p className="mt-6 inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" /> Written answers are read privately by me in the Owner's room — quotes I share end up in the headlines.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-16 flex flex-wrap justify-center gap-4">
          <Link
            to="/#survey"
            data-testid="results-cta-survey"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep transition-colors dark:bg-turf dark:text-background"
          >
            Take the survey
          </Link>
          <Link
            to="/"
            data-testid="results-back-home"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-full border-2 border-pitch/25 dark:border-turf/40 font-semibold text-pitch dark:text-turf hover:border-pitch dark:hover:border-turf transition-all"
          >
            <ArrowLeft className="h-4 w-4" /> Back to the project
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Results;
